import SearchIcon from 'front/assets/other-icons/search.svg';
import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { searchPageFieldClassName } from './lib/SearchPageFieldStyled';
import type { SearchPageFieldPropsType } from './types';

const setInputValueBasedOnParam = () => {
  if (window.URLSearchParams) {
    const params = new URLSearchParams(location.search.substring(1));
    return params.get('search') || '';
  }
  return '';
};

const SearchPageField: React.FC<SearchPageFieldPropsType> = ({ searchUrl }) => {
  const navigate = useNavigate();
  const [inputValue, setInputValue] = useState(setInputValueBasedOnParam);
  const [isFocused, setIsFocused] = useState(false);

  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchButtonRef = useRef<HTMLButtonElement>(null);

  const focusInput = useCallback(() => {
    setIsFocused(true);
    if (searchInputRef.current) {
      searchInputRef.current.focus();
      searchInputRef.current.setSelectionRange(0, searchInputRef.current.value.length);
    }
  }, []);

  const getNewLocationDescriptor = useCallback(
    () => ({
      pathname: searchUrl,
      search: `?search=${encodeURIComponent(inputValue.trim())}`,
    }),
    [searchUrl, inputValue],
  );

  const handleBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    e.preventDefault();

    if (e.relatedTarget === searchButtonRef.current) {
      return;
    }

    setIsFocused(false);
  }, []);

  const handleClick = useCallback(
    (e: React.FormEvent<HTMLButtonElement | HTMLInputElement>) => {
      e.preventDefault();

      if (!isFocused || !inputValue.length) {
        focusInput();
        return;
      }

      const locationDescriptor = getNewLocationDescriptor();
      navigate(locationDescriptor);

      setIsFocused(false);
    },
    [isFocused, inputValue, focusInput, getNewLocationDescriptor, navigate],
  );

  const handleChange = useCallback((e: React.FormEvent<HTMLInputElement>) => {
    setInputValue(e.currentTarget.value);
  }, []);

  const handleEnter = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        handleClick(e as any);
      }
    },
    [handleClick],
  );

  return (
    <div className={searchPageFieldClassName} data-focused={String(isFocused)}>
      <input onBlur={handleBlur} onChange={handleChange} onKeyPress={handleEnter} ref={searchInputRef} type="search" value={inputValue} />
      <button type="button" onClick={handleClick} ref={searchButtonRef}>
        <SearchIcon />
      </button>
    </div>
  );
};

export { SearchPageField };
